/**
 * Alshallal Pastry Company HR System - Dashboard Page
 * Phase 6: Dashboards & UX Enhancements
 * 
 * Role-based dashboard with real data from /api/dashboard/stats
 * - Employee: Leave balances, requests summary, documents summary, quick actions
 * - Manager: + Direct reports pending approvals
 * - HR: + All pending queues, expiry alerts, export shortcuts
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Users, 
  User,
  FileText, 
  Calendar, 
  ClipboardList,
  Activity,
  ArrowRight,
  ArrowLeft,
  AlertTriangle,
  Clock,
  CheckCircle,
  XCircle,
  Download,
  Plus,
  Upload,
  RefreshCw,
  Loader2,
  CalendarPlus,
  FileSearch,
  Bell
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import ExpiryAlertsWidget from '@/components/documents/ExpiryAlertsWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// ============== Loading Skeleton Components ==============

const StatCardSkeleton = () => (
  <Card className="bg-white border-slate-200">
    <CardHeader className="pb-2">
      <Skeleton className="h-4 w-24" />
    </CardHeader>
    <CardContent>
      <Skeleton className="h-8 w-16 mb-2" />
      <Skeleton className="h-4 w-32" />
    </CardContent>
  </Card>
);

const WidgetSkeleton = () => (
  <Card className="bg-white border-slate-200">
    <CardHeader>
      <Skeleton className="h-5 w-40" />
    </CardHeader>
    <CardContent className="space-y-3">
      <Skeleton className="h-12 w-full" />
      <Skeleton className="h-12 w-full" />
    </CardContent>
  </Card>
);

// ============== Empty State Component ==============

const EmptyState = ({ icon: Icon, message, actionLabel, onAction }) => (
  <div className="brand-empty-state">
    <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-beige-60 mb-3">
      <Icon className="h-6 w-6 text-brand-brown/70" />
    </div>
    <p className="text-sm text-muted-foreground mb-3">{message}</p>
    {actionLabel && onAction && (
      <Button size="sm" variant="outline" onClick={onAction}>
        {actionLabel}
      </Button>
    )}
  </div>
);

// ============== Stat Card Component ==============

const StatCard = ({ label, value, subLabel, icon: Icon, color = 'slate', onClick }) => {
  // Map legacy color names → Alshallal brand color tints used as soft icon backgrounds.
  const colorClasses = {
    slate: 'bg-brand-beige-60 text-brand-black',
    amber: 'bg-[hsl(30_63%_50%/0.14)] text-brand-brown',
    green: 'bg-[hsl(140_40%_92%)] text-[hsl(140_50%_24%)]',
    red:   'bg-[hsl(18_84%_30%/0.12)] text-brand-brown',
    blue:  'bg-[hsl(202_56%_83%/0.55)] text-[hsl(202_50%_25%)]',
  };

  return (
    <Card
      className={cn(
        "border-border bg-card",
        onClick && "brand-card-interactive"
      )}
      onClick={onClick}
    >
      <CardHeader className="pb-2">
        <CardDescription className="flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">
          {Icon && (
            <span className={cn("inline-flex items-center justify-center h-7 w-7 rounded-lg", colorClasses[color])}>
              <Icon className="h-3.5 w-3.5" />
            </span>
          )}
          {label}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-bold text-brand-black tabular-nums tracking-tight">{value}</div>
        {subLabel && (
          <p className="text-sm text-muted-foreground mt-1">{subLabel}</p>
        )}
      </CardContent>
    </Card>
  );
};

// ============== Queue Item Component ==============

const QueueItem = ({ label, count, path, icon: Icon, color = 'amber' }) => {
  const navigate = useNavigate();
  const colorClasses = {
    amber: 'bg-[hsl(30_63%_50%/0.10)] text-brand-brown border-[hsl(30_63%_50%/0.35)] hover:border-brand-orange/60',
    blue:  'bg-[hsl(202_56%_83%/0.40)] text-[hsl(202_50%_25%)] border-brand-sky hover:border-[hsl(202_56%_70%)]',
    green: 'bg-[hsl(140_40%_92%)] text-[hsl(140_50%_24%)] border-[hsl(140_40%_82%)] hover:border-[hsl(140_50%_55%)]',
    red:   'bg-[hsl(18_84%_30%/0.08)] text-brand-brown border-[hsl(18_84%_30%/0.30)] hover:border-brand-brown'
  };

  return (
    <div 
      className={cn(
        "flex items-center justify-between p-3.5 rounded-xl border cursor-pointer transition-all duration-200",
        "hover:shadow-brand-md hover:-translate-y-px",
        colorClasses[color]
      )}
      onClick={() => navigate(path)}
    >
      <div className="flex items-center gap-3">
        <Icon className="h-5 w-5 flex-shrink-0" />
        <span className="font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="text-2xl font-bold tabular-nums">{count}</span>
        <ArrowRight className="h-4 w-4 opacity-50" />
      </div>
    </div>
  );
};

// ============== Main Dashboard Component ==============

const DashboardPage = () => {
  const { user, hasRole, token } = useAuth();
  const { t, isRTL, getRoleDisplay } = useLanguage();
  const navigate = useNavigate();

  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const ArrowIcon = isRTL ? ArrowLeft : ArrowRight;

  // Fetch dashboard stats
  const fetchStats = useCallback(async () => {
    if (!token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_URL}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setStats(response.data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Failed to fetch dashboard stats:', err);
      setError(err.response?.data?.detail?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  // Get user display name
  const getDisplayName = () => {
    if (!user) return '';
    if (isRTL && user.first_name_ar) {
      return user.first_name_ar;
    }
    return user.first_name;
  };

  // Role checks
  const isManager = hasRole('manager');
  const isHR = hasRole('hr_assistant', 'hr_manager', 'hr_admin', 'super_admin');
  const isAdmin = hasRole('hr_admin', 'super_admin');

  // Quick actions for employees — brand-aligned tints (no off-brand greens/blues)
  const employeeQuickActions = [
    {
      key: 'request-leave',
      icon: CalendarPlus,
      label: isRTL ? 'طلب إجازة' : 'Request Leave',
      path: '/leave',
      color: 'bg-[hsl(202_56%_83%/0.50)] text-[hsl(202_55%_22%)] hover:border-brand-sky'
    },
    {
      key: 'new-request',
      icon: Plus,
      label: isRTL ? 'طلب جديد' : 'New Request',
      path: '/requests',
      color: 'bg-[hsl(30_63%_50%/0.12)] text-brand-brown hover:border-brand-orange/60'
    }
  ];

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="dashboard-page">
      {/* Welcome Section - responsive */}
      <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">
            {isRTL ? `مرحباً، ${getDisplayName()}` : `Welcome back, ${getDisplayName()}`}
          </h1>
          <p className="text-sm sm:text-base text-slate-600 mt-1">
            {getRoleDisplay(user?.role)}
          </p>
        </div>
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={fetchStats} 
          disabled={loading}
          className="text-slate-500 self-start"
        >
          <RefreshCw className={cn("h-4 w-4 me-1", loading && "animate-spin")} />
          {isRTL ? 'تحديث' : 'Refresh'}
        </Button>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-500 flex-shrink-0" />
          <div>
            <p className="text-red-700">{error}</p>
            <Button variant="link" size="sm" onClick={fetchStats} className="text-red-600 p-0 h-auto">
              {isRTL ? 'إعادة المحاولة' : 'Try again'}
            </Button>
          </div>
        </div>
      )}

      {/* Employee Quick Actions - responsive wrap */}
      <div className="flex flex-wrap gap-2 sm:gap-3">
        {employeeQuickActions.map(action => (
          <Button
            key={action.key}
            variant="outline"
            onClick={() => navigate(action.path)}
            className={cn(
              "gap-2 border-2 flex-1 sm:flex-none min-w-[140px] font-semibold transition-all",
              action.color
            )}
          >
            <action.icon className="h-4 w-4" />
            {action.label}
          </Button>
        ))}
      </div>

      {/* Loading State */}
      {loading && !stats && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map(i => <StatCardSkeleton key={i} />)}
          </div>
          <WidgetSkeleton />
        </div>
      )}

      {/* Employee Stats Section */}
      {stats?.employee && (
        <>
          {/* Leave Balances — HR only. Regular employees / managers must
              not see their own balance numbers per product decision
              (2026-02-16). Backend also strips leave_balances for non-HR. */}
          {isHR && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
              <Calendar className="h-5 w-5 text-slate-600" />
              {isRTL ? 'رصيد الإجازات' : 'Leave Balances'}
            </h2>
            {stats.employee.leave_balances.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="dashboard-leave-balance-grid">
                {stats.employee.leave_balances
                  .filter(b => b.type_name !== 'Unknown' && b.total > 0)
                  .map((balance, idx) => (
                    <Card key={idx} className="bg-white border-slate-200">
                      <CardContent className="p-4">
                        <p className="text-sm text-slate-600 mb-1">{balance.type_name}</p>
                        <div className="flex items-baseline gap-1">
                          <span className="text-2xl font-bold text-slate-900">{balance.available}</span>
                          <span className="text-sm text-slate-500">/ {balance.total}</span>
                        </div>
                        {balance.pending > 0 && (
                          <p className="text-xs text-amber-600 mt-1">
                            {balance.pending} {isRTL ? 'معلق' : 'pending'}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  ))}
              </div>
            ) : (
              <EmptyState 
                icon={Calendar}
                message={isRTL ? 'لا توجد أرصدة إجازات' : 'No leave balances allocated'}
              />
            )}
          </div>
          )}

          {/* Summary Stats Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Leave Summary */}
            <Card className="bg-white border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  {isRTL ? 'ملخص الإجازات' : 'Leave Summary'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-xl font-bold text-amber-600">{stats.employee.leave_summary.pending}</div>
                    <p className="text-xs text-slate-500">{isRTL ? 'معلق' : 'Pending'}</p>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-green-600">{stats.employee.leave_summary.approved}</div>
                    <p className="text-xs text-slate-500">{isRTL ? 'موافق' : 'Approved'}</p>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-red-600">{stats.employee.leave_summary.rejected}</div>
                    <p className="text-xs text-slate-500">{isRTL ? 'مرفوض' : 'Rejected'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Requests Summary */}
            <Card className="bg-white border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                  <ClipboardList className="h-4 w-4" />
                  {isRTL ? 'ملخص الطلبات' : 'Requests Summary'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-xl font-bold text-amber-600">{stats.employee.requests_summary.pending}</div>
                    <p className="text-xs text-slate-500">{isRTL ? 'معلق' : 'Pending'}</p>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-green-600">{stats.employee.requests_summary.approved}</div>
                    <p className="text-xs text-slate-500">{isRTL ? 'موافق' : 'Approved'}</p>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-red-600">{stats.employee.requests_summary.rejected}</div>
                    <p className="text-xs text-slate-500">{isRTL ? 'مرفوض' : 'Rejected'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Documents Summary */}
            <Card className="bg-white border-slate-200">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-slate-600 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {isRTL ? 'ملخص المستندات' : 'Documents Summary'}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <div className="text-xl font-bold text-amber-600">{stats.employee.documents_summary.pending}</div>
                    <p className="text-xs text-slate-500">{isRTL ? 'معلق' : 'Pending'}</p>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-green-600">{stats.employee.documents_summary.verified}</div>
                    <p className="text-xs text-slate-500">{isRTL ? 'موثق' : 'Verified'}</p>
                  </div>
                  <div>
                    <div className="text-xl font-bold text-red-600">
                      {stats.employee.documents_summary.expiring_soon}
                    </div>
                    <p className="text-xs text-slate-500">{isRTL ? 'ينتهي قريباً' : 'Expiring'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </>
      )}

      {/* Manager Section */}
      {isManager && stats?.manager && (
        <div>
          <h2 className="text-lg font-semibold text-slate-900 mb-3 flex items-center gap-2">
            <Users className="h-5 w-5 text-slate-600" />
            {isRTL ? 'موافقات الفريق' : 'Team Approvals'}
            {stats.manager.direct_reports_count > 0 && (
              <span className="text-sm font-normal text-slate-500">
                ({stats.manager.direct_reports_count} {isRTL ? 'تقارير مباشرة' : 'direct reports'})
              </span>
            )}
          </h2>
          
          {(stats.manager.leave_pending > 0 || stats.manager.requests_pending > 0) ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <QueueItem
                icon={Calendar}
                label={isRTL ? 'طلبات الإجازات' : 'Leave Requests'}
                count={stats.manager.leave_pending}
                path="/leave/approvals"
                color="amber"
              />
              <QueueItem
                icon={ClipboardList}
                label={isRTL ? 'الطلبات العامة' : 'General Requests'}
                count={stats.manager.requests_pending}
                path="/requests/approvals"
                color="blue"
              />
            </div>
          ) : (
            <Card className="bg-green-50 border-green-200">
              <CardContent className="py-6 text-center">
                <CheckCircle className="h-8 w-8 text-green-500 mx-auto mb-2" />
                <p className="text-green-700 font-medium">
                  {isRTL ? 'لا توجد موافقات معلقة' : 'No pending approvals'}
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* HR Section */}
      {isHR && stats?.hr && (
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
            <Activity className="h-5 w-5 text-slate-600" />
            {isRTL ? 'لوحة الموارد البشرية' : 'HR Dashboard'}
          </h2>

          {/* HR Pending Queues */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <QueueItem
              icon={FileSearch}
              label={isRTL ? 'مراجعة المستندات' : 'Document Reviews'}
              count={stats.hr.documents_pending_review}
              path="/documents/verification"
              color="amber"
            />
            <QueueItem
              icon={Calendar}
              label={isRTL ? 'إجازات بانتظار HR' : 'Leave Pending HR'}
              count={stats.hr.leave_pending_hr}
              path="/leave/admin"
              color="blue"
            />
            <QueueItem
              icon={ClipboardList}
              label={isRTL ? 'طلبات بانتظار HR' : 'Requests Pending HR'}
              count={stats.hr.requests_pending_hr}
              path="/requests/admin"
              color="blue"
            />
          </div>

          {/* HR Stats Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={Users}
              label={isRTL ? 'إجمالي الموظفين' : 'Total Employees'}
              value={stats.hr.total_employees}
              color="slate"
              onClick={() => navigate('/employees')}
            />
            <StatCard
              icon={AlertTriangle}
              label={isRTL ? 'مستندات منتهية' : 'Expired Documents'}
              value={stats.hr.documents_expired}
              color={stats.hr.documents_expired > 0 ? 'red' : 'green'}
            />
            <StatCard
              icon={Bell}
              label={isRTL ? 'ينتهي قريباً (30 يوم)' : 'Expiring Soon (30d)'}
              value={stats.hr.documents_expiring_soon}
              color={stats.hr.documents_expiring_soon > 0 ? 'amber' : 'green'}
            />
            <Card 
              className="bg-white border-slate-200 hover:border-slate-300 hover:shadow-md transition-all cursor-pointer"
              onClick={() => navigate('/employees')}
            >
              <CardHeader className="pb-2">
                <CardDescription className="flex items-center gap-2">
                  <span className="p-1 rounded bg-blue-100 text-blue-600">
                    <Download className="h-4 w-4" />
                  </span>
                  {isRTL ? 'تصدير البيانات' : 'Export Data'}
                </CardDescription>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-600">
                  {isRTL ? 'انتقل لصفحة الموظفين للتصدير' : 'Go to Employees to export'}
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Document Expiry Alerts Widget */}
          <ExpiryAlertsWidget />
        </div>
      )}

      {/* Quick Navigation */}
      <div>
        <h2 className="text-lg font-semibold text-slate-900 mb-3">
          {isRTL ? 'التنقل السريع' : 'Quick Navigation'}
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button variant="outline" className="h-auto py-3 flex-col gap-1" onClick={() => navigate('/leave')}>
            <Calendar className="h-5 w-5" />
            <span className="text-sm">{isRTL ? 'إجازاتي' : 'My Leave'}</span>
          </Button>
          <Button variant="outline" className="h-auto py-3 flex-col gap-1" onClick={() => navigate('/requests')}>
            <ClipboardList className="h-5 w-5" />
            <span className="text-sm">{isRTL ? 'طلباتي' : 'My Requests'}</span>
          </Button>
          {/* My Profile - documents section available for all users */}
          <Button variant="outline" className="h-auto py-3 flex-col gap-1" onClick={() => navigate('/my-profile')}>
            <User className="h-5 w-5" />
            <span className="text-sm">{isRTL ? 'ملفي الشخصي' : 'My Profile'}</span>
          </Button>
          {isHR && (
            <Button variant="outline" className="h-auto py-3 flex-col gap-1" onClick={() => navigate('/employees')}>
              <Users className="h-5 w-5" />
              <span className="text-sm">{isRTL ? 'الموظفون' : 'Employees'}</span>
            </Button>
          )}
          {isAdmin && (
            <Button variant="outline" className="h-auto py-3 flex-col gap-1" onClick={() => navigate('/audit')}>
              <Activity className="h-5 w-5" />
              <span className="text-sm">{isRTL ? 'سجلات النظام' : 'Audit Logs'}</span>
            </Button>
          )}
        </div>
      </div>

      {/* Cache Info */}
      {lastRefresh && (
        <p className="text-xs text-slate-400 text-center">
          {isRTL ? 'آخر تحديث:' : 'Last updated:'} {lastRefresh.toLocaleTimeString()}
          {stats?.generated_at && (
            <span className="ms-2">
              ({isRTL ? 'من الخادم:' : 'Server:'} {new Date(stats.generated_at).toLocaleTimeString()})
            </span>
          )}
        </p>
      )}
    </div>
  );
};

export default DashboardPage;
